import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import classNames from 'classnames';
import ProfileImage from '../../other/ProfileImage';
import { Dropdown } from '@widget/popups/Dropdown';
import ItemCard from '../ItemCard';
import { ObjectInfoCard, isObjectInfoCardProps } from '../Tables/object-search/ObjectInfoCardComponent';
import { useUXPContext } from '@core/context/useUXPContext';
/**
 * Base component for rendering stacked profile images with dropdown
 * Internal use only - not exported
 */
const InfoCardGroupBase = (props) => {
    const context = useUXPContext();
    const dropdownHandleRef = useRef(null);
    const { items = [], fields = {}, details, maxVisible = 5, size = 'medium', shape = 'circle', dropdownPosition = 'bottom-left', className, style, renderDropdownItem, dropdownFooter, dropdownContent, dropdownContentClassName, dropdownMinWidth, dropdownMaxWidth, enableAddButton = false } = props;
    // Calculate visible and remaining items
    const visibleItems = useMemo(() => {
        return items.slice(0, maxVisible);
    }, [items, maxVisible]);
    const remainingCount = useMemo(() => {
        return Math.max(0, items.length - maxVisible);
    }, [items.length, maxVisible]);
    // Extract profile image props from item
    const getProfileImageProps = (item) => {
        if (!item)
            return {};
        return {
            image: fields.image ? item[fields.image] : undefined,
            name: fields.name ? item[fields.name] : undefined
        };
    };
    // Extract ItemCard props from item
    const getItemCardProps = (item) => {
        if (!item)
            return {};
        return {
            image: fields.image ? item[fields.image] : undefined,
            title: fields.title ? item[fields.title] : undefined,
            subTitle: fields.subtitle ? item[fields.subtitle] : undefined,
            name: fields.name ? item[fields.name] : undefined
        };
    };
    // Render details content for an item
    const renderDetails = (item) => {
        if (!item || !details)
            return null;
        if (typeof details === 'function') {
            const result = details(item);
            // Check if result is ObjectInfoCardProps
            if (isObjectInfoCardProps(result)) {
                return _jsx(ObjectInfoCard, Object.assign({}, result, { layout: 'vertical', columns: 1 }));
            }
            return result;
        }
        return details;
    };
    // Render stacked profile images
    const renderStackedImages = () => {
        if ((items === null || items === void 0 ? void 0 : items.length) === 0) {
            return _jsx("div", { className: "uxpcore_infocardgroup__stack", children: enableAddButton
                    ? _jsx(ProfileImage, { name: `+`, size: size, shape: shape, bgColor: context.theme.secondaryBGColor, textColor: context.theme.secondaryTextColor, skipAcronym: true, borderColor: context.theme.secondaryTextColor, borderWidth: '1px' })
                    : _jsx(ProfileImage, { size: size, shape: shape }) });
        }
        return (_jsxs("div", { className: "uxpcore_infocardgroup__stack", children: [visibleItems.map((item, index) => (_jsx("div", { className: "uxpcore_infocardgroup__stack-item", style: { zIndex: index }, children: _jsx(ProfileImage, Object.assign({}, getProfileImageProps(item), { size: size, shape: shape, borderColor: context.theme.secondaryTextColor, borderWidth: '1px' })) }, index))), remainingCount > 0 && (_jsx("div", { className: "uxpcore_infocardgroup__stack-item uxpcore_infocardgroup__badge", style: { zIndex: maxVisible }, children: _jsx(ProfileImage, { name: `+${remainingCount}`, size: size, shape: shape, bgColor: context.theme.secondaryBGColor, textColor: context.theme.secondaryTextColor, skipAcronym: true, borderColor: context.theme.secondaryTextColor, borderWidth: '1px' }) })), enableAddButton && _jsx(ProfileImage, { name: `+`, size: size, shape: shape, bgColor: context.theme.secondaryBGColor, textColor: context.theme.secondaryTextColor, skipAcronym: true, borderColor: context.theme.secondaryTextColor, borderWidth: '1px' })] }));
    };
    // Render dropdown content with all items as ItemCards
    const renderDropdownContent = () => {
        return (_jsxs("div", { className: "uxpcore_infocardgroup__dropdown", children: [_jsx("div", { className: "uxpcore_infocardgroup__items", children: items.map((item, index) => {
                        const itemCardContent = (_jsx(ItemCard, Object.assign({}, getItemCardProps(item), { size: size, shape: shape })));
                        // Allow custom rendering via prop
                        const content = renderDropdownItem
                            ? renderDropdownItem(item, index, itemCardContent)
                            : itemCardContent;
                        // If details available, wrap ItemCard in nested Dropdown
                        if (details) {
                            return (_jsx("div", { className: "uxpcore_infocardgroup__item", children: _jsx(Dropdown, { trigger: content, content: _jsx("div", { className: "uxpcore_infocardgroup__details", children: renderDetails(item) }), position: "right-top" }) }, index));
                        }
                        // No details, just render content
                        return (_jsx("div", { className: "uxpcore_infocardgroup__item", children: content }, index));
                    }) }), dropdownFooter && (_jsx("div", { className: "uxpcore_infocardgroup__footer", children: dropdownFooter }))] }));
    };
    const resolvedContent = dropdownContent === undefined
        ? renderDropdownContent()
        : (typeof dropdownContent === 'function'
            ? dropdownContent({ close: () => { var _a; return (_a = dropdownHandleRef.current) === null || _a === void 0 ? void 0 : _a.close(); } })
            : dropdownContent);
    return (_jsx("div", { className: classNames('uxpcore_infocardgroup', className), style: style, children: _jsx(Dropdown, { ref: dropdownHandleRef, trigger: renderStackedImages(), content: resolvedContent, position: dropdownPosition, contentClassName: dropdownContentClassName, minWidth: dropdownMinWidth, maxWidth: dropdownMaxWidth }) }));
};
export default InfoCardGroupBase;
